import React, { useMemo, useState } from "react";
import Permission from "./PermissionType";
import {
    FlexContainer,
    UpperControls,
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableActionButton,
    DataTableCell,
    DataTableContainer,
    DataTableEditActionButton,
    DataTableHeaderCell,
    DataTableViewActionButton,
    Text,
    Toast,
    mobile,
    tablet,
    PortalSearchInput,
    PortalSelect,
    PortalExportLink,
    PortalTableActions,
    PortalTableControls
} from "src/components";
import Checkbox from "./Checkbox";
import UpArrow from "src/img/employeeList/SortUpArrow.svg";
import DownArrow from "src/img/employeeList/SortDownArrow.svg";
import UserIcon from "src/img/new/user-only.png";

import { ACCOUNT_OWNER, MULTIPLE_PERMISSIONS, NO_ROLE } from "src/constants/roles";
import { getCompanySubscriptionExpiryString } from "src/utils/dates";
import styled from "styled-components";
import { capitalizeString } from "src/utils/helpers";
import { UserContext } from "src/scenes/App/UserContext";
import { API_URL } from "src/scenes/App";
import { SortableColumns, SortableColumnState } from "../EmployeeList";
import pluralize from "pluralize";
import { SubscriptionMode, UserKeys } from "src/constants/userDetails";
import { PageType } from "src/scenes/EmployeeList/EmployeeList";
import { CHECKLISTS, hasChatPermission, TEAM_GOALS } from "src/constants/permissions";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";

export const ButtonsFlexWrap = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-start;
    gap: 10px;

    ${mobile`
        flex-direction: row;
        align-items: flex-start;
        gap: 10px;
        margin-top: 10px;
    `}

     ${tablet`
        flex-direction: row;
        align-items: flex-start;
        gap: 10px;
        margin-top: 10px;
    `}
`;


const HeaderTh = styled(DataTableHeaderCell)`
    cursor: pointer;
    :hover {
        opacity: 0.8;
    }
`;

const RoleBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding: 0.3rem 1.8rem 0.5rem 1.8rem;
    border-radius: 7px;
    max-height: 39px;
    width: 100px;

    font-size: 14px;
    font-weight: 500;

    background: ${({ assigned }) => (assigned ? "rgba(91, 110, 245, 0.12)" : "#E8E8E8")};
    color: ${({ assigned }) => (assigned ? "#5B6EF5" : "#626262")};
`;

const BaseUserActionButton = styled.button`
    width: ${({ width }) => width || "100px"};
    height: ${({ height }) => height || "32px"};

    background: ${({ background }) => background || "#FFFFFF"};
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid ${({ borderColor }) => borderColor || "#000"};
    border-radius: 7px;
    opacity: 1;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
    white-space: nowrap;

    /* Text style */
    text-align: center;
    font-family: Manrope;
    font-size: ${({ fontSize }) => fontSize || "12px"};
    line-height: ${({ lineHeight }) => lineHeight || "14px"};
    font-weight: 500;
    letter-spacing: 0px;
    color: ${({ color }) => color || "#000"};

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const DeleteUserButton = styled(BaseUserActionButton)`
    width: 110px;
    height: 39px;

    background: #FFF5F5;
    border-color: #FF3B3B;
    color: #FF3B3B;

    font-size: 14px;
    line-height: 16px;
`;

const TableActionButton = styled(DataTableActionButton)`
    width: 94px;
    height: 32px;
    min-width: 94px;
    background: ${({ backgroundColor }) => backgroundColor || "#5B6EF5"};
    color: ${({ color }) => color || "#FFFFFF"};
    border: ${({ border }) => border || "1px solid transparent"};
    border-radius: 7px;
    box-shadow: none;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Count = styled.span`
    text-align: left;
    font: normal normal medium 14px/16px Manrope;
    letter-spacing: 0px;
    color: var(--calendar-text, #171A22);
    opacity: 1;
`;

const AccessTokenButton = styled(Button)`
    min-width: 100px;
    width: 100%;
    max-width: 8em;
    padding: 0.6em 0.7em;
    font-size: 12px;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 600;
    border-radius: 7px !important;
    border: none;
    box-shadow: none;
    cursor: auto;
`;

const UserListsTable = (props) => {
    const userContext = React.useContext(UserContext);
    const { 
        sortColumn, 
        sortableColumnStates, 
        openPermissionForm, 
        openAddNewUserOptions, 
        openUserDetailsForm, 
        removeUsers, 
        openSendInviteForm, 
        isShowingClients, 
        customNames, 
        onFilterChange, 
        existedUsers,
        openUserUploadsModal,
        roles = [],
    } = props;
    const [ selectedUsers, setSelectedUsers ] = useState([]);
    const [ searchQuery, setSearchQuery ] = useState("");

    const users = useMemo(() => {
        if (!searchQuery) {
            return props.users;
        }

        const normalizedSearchQuery = searchQuery.toLowerCase();
        return props.users.filter(item => 
            item.firstName.toLowerCase().includes(normalizedSearchQuery) || item.lastName.toLowerCase().includes(normalizedSearchQuery) 
        );
    }, [props.users, searchQuery]);

    const showLeadershipRolesColumn = hasChatPermission(
        userContext?.permissions
    );
    const hiddenPermissionIds = [
        ...(!hasFeature(userContext, FEATURE_FLAGS.TEAM_GOALS) ? [TEAM_GOALS] : []),
        ...(!hasFeature(userContext, FEATURE_FLAGS.CHECKLIST) ? [CHECKLISTS] : [])
    ];
    const hiddenPermissionIdSet = new Set(hiddenPermissionIds);

    const tbWidths = ["5%", "9%", "9%", "16%", "16%", "14%", "7%", "8%", "10%", "8%", "8%", "8%", "8%", "7%", "8%", "12%"];

    const renderUserStatus = (status, hasReceivedInvitation) => {
        return status ? <Text color="#36BE24" align="left">Active</Text>
            :
            hasReceivedInvitation ? <Text color="var(--calendar-text, #171A22)" align="left">Pending</Text>
                :
                <Text color="#FF3B3B" align="left">Inactive</Text>;
    };

    const renderUserInviteStatus = (user) => {
        const InviteSent = selectedUsers.length === 1 && checkIfUuidIsSelected(user.uuid) 
            ? <TableActionButton onClick={() => handleSendInvite(user)} backgroundColor="var(--calendar-surface, #FFFFFF)" color="var(--calendar-text, #171A22)" border="1px solid var(--calendar-text, #171A22)"> Resend Invite</TableActionButton>
            : <span style={{ marginLeft: "10px" }}>Invite Sent</span>;
        return user.status ? <div style={{ fontWeight: "500", textAlign: "center" }}>N/A</div>
            : user.hasReceivedInvitation ? InviteSent
                : <TableActionButton onClick={() => handleSendInvite(user)}> Send Invite</TableActionButton>;
    };

    const renderUserDocuments = (user) => {
        const requiredDocs = user.requiredDocuments || [];
        if (requiredDocs.length === 0) {
            return <Text align="center">-</Text>;
        }

        const uploadedCount = requiredDocs.filter(doc => doc?.upload?.url).length;
        const approvedCount = requiredDocs.filter(
            doc => doc?.upload?.approvedAt && doc?.upload?.approvedBy
        ).length;
        const totalCount = requiredDocs.length;
        const isMismatch = approvedCount < uploadedCount;

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <DataTableViewActionButton onClick={() => openUserUploadsModal(user)}>
                    View Files
                </DataTableViewActionButton>
                <Count style={{ color: isMismatch ? "#FF3B3B" : "var(--calendar-text, #171A22)" }}>
                    {approvedCount} of {totalCount} Confirmed
                </Count>
            </div>
        );
    };

    const emitOpenPermissionForm = (user) => {
        const dataToEmit = {
            uuid: user.uuid,
            name: capitalizeString(user.firstName) + " " + capitalizeString(user.lastName),
            email: user.email,
            isClient: user.isClient,
            leadershipRoles: user.leadershipRoles || [],
            // because users with no role and permission record in the db will have `roleAndPermissions: null`
            role: resolveUserRole(user),
            permissions: getUserPermissionIds(user)
        };
        return openPermissionForm(dataToEmit);
    };

    const emitOpenUserDetailsForm = user => {
        // returns null when "Add New User" button is clicked
        return openUserDetailsForm(user);
    };

    /** @param {string} heading */
    const emitSortColumn = (heading) => {
        
        heading = heading.replace(/ /g, "_").toUpperCase();
        return sortColumn(heading);
    };

    // this is for the checkboxes
    const checkIfUuidIsSelected = (uuid) => selectedUsers.some(obj => obj["uuid"] === uuid);
    
    const addOrRemoveUserFromSelected = (event, user) => {
        const isAlreadyInList = checkIfUuidIsSelected(user.uuid);

        if (!isAlreadyInList) {
            const data = {
                uuid: user.uuid,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNumber: user.phoneNumber,
                requiredDocuments: user.requiredDocuments || [],
                role: filterUserRole(user)
            };
            setSelectedUsers([...selectedUsers, data]);
        } else {
            setSelectedUsers(selectedUsers.filter((obj) => obj["uuid"] !== user.uuid));
        }
    };

    const handleSearchUser = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSelectAllCheckbox = (e) => {
        const isChecked = !e.target.checked;
        
        if (!isChecked) {
            setSelectedUsers([]);
        }
        else {
            const arr = users.map(user => ({
                uuid: user.uuid,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNumber: user.phoneNumber,
                requiredDocuments: user.requiredDocuments || [],
                role: user.roleAndPermissions == null ? NO_ROLE : user.roleAndPermissions.roleId
            }));
            setSelectedUsers(arr);
        }
    };

    const handleUserRemoval = () => {
        // account owner roles cannot be deleted.
        const result = checkIfSelectedUsersContainsAccountOwner();
        if (result.length > 0) {
            return Toast.error("Cannot remove users with Account Owner permission. Please de-select such users first.");
        }
        else {
            let userUuidsForDeletion = [];
            selectedUsers.forEach(user => userUuidsForDeletion.push(user.uuid));
            return removeUsers(userUuidsForDeletion);
        }
    };

    const handleSendInvite = (user = null) => {
        const dataToSend = user !== null ? [user] : selectedUsers;
        return openSendInviteForm(dataToSend);
    };

    const checkIfSelectedUsersContainsAccountOwner = () => selectedUsers.filter(obj => obj["role"] === 1);

    const filterUserRole = (user) => user.roleAndPermissions == null ? NO_ROLE : user.roleAndPermissions.roleId;

    const getUserPermissionIds = (user) => {
        if (!user.roleAndPermissions?.permissionIds) {
            return [];
        }

        try {
            return JSON.parse(user.roleAndPermissions.permissionIds);
        } catch (error) {
            console.log(error);
            return [];
        }
    };

    const normalizePermissionIds = (permissionIds) => (
        Array.isArray(permissionIds)
            ? permissionIds
                .map(Number)
                .filter(permissionId => !hiddenPermissionIdSet.has(permissionId))
                .sort((a, b) => a - b)
            : []
    );

    const permissionIdsMatch = (first, second) => {
        const normalizedFirst = normalizePermissionIds(first);
        const normalizedSecond = normalizePermissionIds(second);

        return normalizedFirst.length === normalizedSecond.length &&
            normalizedFirst.every((permissionId, index) => permissionId === normalizedSecond[index]);
    };

    const resolveUserRole = (user) => {
        const roleId = filterUserRole(user);

        if (roleId === NO_ROLE || roleId === ACCOUNT_OWNER) {
            return roleId;
        }

        const permissionIds = getUserPermissionIds(user);
        const matchingRole = roles.find(role => permissionIdsMatch(permissionIds, role.permissionIds));

        return matchingRole ? matchingRole.id : MULTIPLE_PERMISSIONS;
    };

    /**
     * @param {Element|string} heading
     * @param {number} index
     * @param {MouseEvent} event
     */
    const onHeadingClicked = (heading, index, event) => {
        if (typeof heading === "string") {
            emitSortColumn(heading);
        } else if (index === 0) {
            handleSelectAllCheckbox(event);
        }
    };

    const tableHeading = [ 
        // callback is in render - headings. onChange is only for warning prevention
        <Checkbox readOnly checked={selectedUsers.length === users.length} onChange={() => ""} key="0" />,
        "First Name",
        "Last Name",
        "Email",
        "Mobile",
        "User Type",
        "Title",
        isShowingClients ? "Client Type" : "Department",
        "Edit User",
        <span style={{ marginLeft: "15px" }}>Permissions</span>,
        showLeadershipRolesColumn && (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                }}
            >
                <img src={UserIcon} alt="" width="16" height="14" />
                <span>Leadership Roles</span>
            </span>
        ),
        "Uploaded Files",
        "Send Invites",
        "User Access Code",
        "User Status",
        "Subscription Status"
    ];

    const sortableHeading = {
        [SortableColumns.FIRST_NAME]: tableHeading[1],
        [SortableColumns.LAST_NAME]: tableHeading[2],
        [SortableColumns.EMAIL]: tableHeading[3],
        [SortableColumns.MOBILE]: tableHeading[4],
        [SortableColumns.JOB_TITLE]: tableHeading[5],
        [SortableColumns.DEPARTMENT]: tableHeading[6],
        [SortableColumns.USER_STATUS]: tableHeading[14]
    };

    const {
        [UserKeys.SUBSCRIPTION_MODE]: subscriptionMode
    } = userContext;
    const isDemoMode = subscriptionMode === SubscriptionMode.DEMO;
    const expiryString = props.currentSubscription && !isDemoMode ? getCompanySubscriptionExpiryString(props.currentSubscription) : "-";

    const pageType = () => { 
        return isShowingClients ? PageType.CLIENT : PageType.EMPLOYEE; 
    };
    
    const usersList = users.map(user => {
        const displayRole = resolveUserRole(user);
        const hasLeadershipRole = user.leadershipRoles.length > 0;
        return (
            <tr key={user.uuid} className={checkIfUuidIsSelected(user.uuid) ? "selected" : "" } style={{ height: "40px" }}>
                <DataTableCell width={tbWidths[0]} textAlign="center">
                    <label>
                        <Checkbox readOnly
                            checked={checkIfUuidIsSelected(user.uuid)}
                            value={user.uuid}
                            onChange={(e) => addOrRemoveUserFromSelected(e, user)} />
                    </label>
                </DataTableCell>
                <DataTableCell width={tbWidths[1]} className="change-color-on-select">{user.firstName}</DataTableCell>
                <DataTableCell width={tbWidths[2]} className="change-color-on-select">{user.lastName}</DataTableCell>
                <DataTableCell width={tbWidths[3]} className="change-color-on-select">{ user.email }</DataTableCell>
                <DataTableCell width={tbWidths[4]} className="change-color-on-select">{user.phoneNumber}</DataTableCell>
                <DataTableCell width={tbWidths[5]}>{ user.isClient ? `${pluralize.singular(customNames.clients)}` : `${pluralize.singular(customNames.employees)}` }</DataTableCell>
                {!isShowingClients && <DataTableCell width={tbWidths[6]} className="change-color-on-select">{ user.employmentPosition }</DataTableCell>}
                <DataTableCell width={tbWidths[7]} className="change-color-on-select">{ user.isClient ? user.employmentPosition : user.department }</DataTableCell>
                
                <DataTableCell width={tbWidths[8]}>
                    <DataTableEditActionButton
                        onClick={() => emitOpenUserDetailsForm(user)}
                    >
                        Edit User
                    </DataTableEditActionButton>
                </DataTableCell>
                <DataTableCell width={tbWidths[9]}
                    style={{
                        cursor: "pointer",
                        pointerEvents: "auto"
                    }}
                    onClick={() => emitOpenPermissionForm(user)}
                >
                    <div style={{ display: "inline-grid", gridGap: "0.2em" }}>
                        <Permission role={displayRole} isClient={user.isClient} hasLeadershipRole={hasLeadershipRole} fontSize={hasLeadershipRole ? "10px" : ""} />
                    </div>
                </DataTableCell>
                {showLeadershipRolesColumn && (
                    <DataTableCell width={tbWidths[10]} textAlign="center">
                        <RoleBadge assigned={hasLeadershipRole}>
                            {hasLeadershipRole ? "Role Assigned" : "Not Assigned"}
                        </RoleBadge>
                    </DataTableCell>
                )}
                <DataTableCell width={tbWidths[11]} textAlign="center">
                    {renderUserDocuments(user)}
                </DataTableCell>
                <DataTableCell width={tbWidths[12]}>
                    { renderUserInviteStatus(user) }
                </DataTableCell>
                <DataTableCell width={tbWidths[13]} textAlign="center">
                    <AccessTokenButton backgroundColor="#000000" color="#fff">
                        { user.accessToken }
                    </AccessTokenButton>
                </DataTableCell>
                <DataTableCell width={tbWidths[14]}>
                    <div style={{ marginLeft: "1px", }}>{ renderUserStatus(user.status, user.hasReceivedInvitation) }</div>
                </DataTableCell>
                <DataTableCell width={tbWidths[15]}>{ expiryString }</DataTableCell>
            </tr>
        );
    });

    const showEmptyRows = () => {
        const emptyRows = 10 - users.length;
        
        if (emptyRows < 1)
            return;

        return Array.from({ length: emptyRows }, (_, i) => (
            <tr key={"empty_" + i} className="placeholder-row">
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                {!isShowingClients && <DataTableCell $empty>&nbsp;</DataTableCell>}
            </tr>
        ));
    };

    return (
        <FlexContainer>
            <UpperControls>
                <PortalTableControls>
                    <PortalSearchInput
                        placeholder="Search"
                        type="text"
                        onChange={(e) => handleSearchUser(e)}
                        width="30rem"
                    />
                    <PortalSelect
                        value={pageType()}
                        onChange={(e) => onFilterChange(e)}
                        width="12.5rem"
                    >
                        <option value={PageType.EMPLOYEE}>
                            {pluralize(customNames.employees, isShowingClients ? existedUsers - users.length : users.length)} (
                            {isShowingClients ? existedUsers - users.length : users.length})
                        </option>

                        <option value={PageType.CLIENT}>
                            {pluralize(customNames.clients, isShowingClients ? users.length : existedUsers - users.length)} (
                            {isShowingClients ? users.length : existedUsers - users.length})
                        </option>
                    </PortalSelect>
                    {!isShowingClients &&
                        <PortalExportLink
                            url={`${API_URL}/employees/export`}
                            filename={`${pluralize.singular(customNames.employees)}List.csv`}
                            user={userContext}
                            style={{ width: "auto" }}
                        >Export {pluralize.singular(customNames.employees)} List
                        </PortalExportLink>
                    }
                    {isShowingClients &&
                        <PortalExportLink
                            url={`${API_URL}/clients/export`}
                            filename={`${pluralize.singular(customNames.clients)}List.csv`}
                            user={userContext}
                            style={{ width: "auto" }}
                        >Export {pluralize.singular(customNames.clients)} List
                        </PortalExportLink>
                    }
                    { selectedUsers.length ?
                        <DeleteUserButton onClick={() => handleUserRemoval()}>
                            Delete User
                        </DeleteUserButton>
                        : ""
                    }
                    { selectedUsers.length > 0 ?
                        <Button backgroundColor="rgba(91,110,245,0.12)" border="1px solid var(--calendar-text, #171A22)"
                            color="var(--calendar-text, #171A22)" weight="700"
                            borderRadius="7px !important"
                            onClick={() => handleSendInvite()}
                        >Send Invite</Button>
                        : ""
                    }
                </PortalTableControls>
                <PortalTableActions>
                    <ActivateFeatureButton
                        onClick={() => openAddNewUserOptions()} 
                    >Add New Users</ActivateFeatureButton>
                </PortalTableActions>
            </UpperControls>
            <DataTableContainer>
                <DataTable minWidth={showLeadershipRolesColumn ? "1500px" : "1320px"}>
                    <thead>
                        <tr>
                            <HeaderTheads
                                isShowingClients={isShowingClients}
                                sortableColumnStates={sortableColumnStates}
                                sortableHeading={sortableHeading}
                                tableHeading={tableHeading}
                                onHeadingClicked={onHeadingClicked}
                                tbWidths={tbWidths}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        { usersList }
                    
                        { showEmptyRows() }
                    </tbody>
                </DataTable>
            </DataTableContainer>
        </FlexContainer>
    );
};

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    img {
        margin-left: auto; /* Push image to the right */
        padding-left: 5px;
    }
`;

const HeaderTheads = ({ isShowingClients, sortableColumnStates, sortableHeading, tableHeading, onHeadingClicked, tbWidths }) => {
    const nonInactiveIndex = sortableColumnStates.findIndex(item => item !== "INACTIVE");
    const activeSortableHeadingString = sortableHeading[nonInactiveIndex];

    const filteredHeadings = tableHeading.filter(Boolean);
    return filteredHeadings.map((heading, index) => {
        
        if (isShowingClients && heading === "Title")
            return null;

        return (
            <HeaderTh onClick={(e) => onHeadingClicked(heading, index, e)}
                key={index}
                width={tbWidths[index]}
                style={{ textAlign: index > 0 ? "left" : "center" }}
            >
                <HeaderContainer>
                    <span key={index}>{heading}</span>
                    <SortingIcon sortingStatus={activeSortableHeadingString === heading ? sortableColumnStates[nonInactiveIndex] : ""} />
                </HeaderContainer>
            </HeaderTh>
        );
    });
};

const SortingIcon = ({ sortingStatus }) => {
    const imgStyle = {
        filter: "invert(1)",
    };

    if (sortingStatus === SortableColumnState.ASCENDING) {
        return <img src={UpArrow} alt={SortableColumnState.ASCENDING} style={imgStyle} />;
    }

    if (sortingStatus === SortableColumnState.DESCENDING) {
        return <img src={DownArrow} alt={SortableColumnState.ASCENDING} style={imgStyle} />;
    }

    return <></>;
};

export default UserListsTable;
